import { useEffect, useRef, useState } from "react";
import { GradingData } from "../../../components/adminDashboard/GradingCenter/GradingData";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import { StyleSheet, View } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";

import SubmissionAndGradingTab from "./SubmissionAndGradingTab";
import AttemptHistoryTab from "./AttemptHistoryTab";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useAppConfig } from "../../../AppConfigProvider";
import TabNavigator from "../../../components/customCoursework/GradeModalTabNavigator/TabNavigator";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { UpdatedEntityType, type UpdateAssessmentSubmissionUpdatedGrade } from "../../../graphql/generated/graphql";

export interface GradeTree {
  id: string;
  totalPoints: string;
  maximumPoints: number;
  questions: {
    id: string;
    totalPoints: string;
    maximumPoints: number;
    rubrics: {
      id: string;
      pointsAwarded: string;
      maxPoints: number;
    }[];
  }[];
}

const AssessmentGradingModalContainer = ({
  isVisible,
  onClose,
  gradingData,
  isLoading,
  canEditScore,
  onSave,
  isSubmitting,
  updateError,
}: {
  isVisible: boolean;
  onClose: () => void;
  gradingData: GradingData | null;
  isLoading: boolean;
  canEditScore: boolean;
  onSave?: (
    gradingData: GradingData,
    updatedGradeEntry: UpdateAssessmentSubmissionUpdatedGrade,
    closeModal: boolean
  ) => Promise<void>;
  isSubmitting?: boolean;
  updateError?: string | null;
}) => {
  const [activeTab, setActiveTab] = useState<string>("submissionAndGrading");

  const [gradeTree, setGradeTree] = useState<GradeTree | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  const lastSavedState = useRef<string>("");

  const { showToast } = useToast();

  useEffect(() => {
    if (!isSaving && gradingData) {
      setGradeTree({
        id: gradingData?.id,
        totalPoints: gradingData?.totalPoints?.toString() ?? "0",
        maximumPoints: gradingData?.maximumPoints ?? 0,
        questions: gradingData?.questions?.map((question) => ({
          id: question.id,
          totalPoints: question.userAnswer.totalPoints?.toString() ?? "0",
          maximumPoints: question.userAnswer.maximumPoints ?? 0,
          rubrics: question.rubrics.map((rubric) => ({
            id: rubric.id,
            pointsAwarded: rubric.pointsAwarded?.toString() ?? "0",
            maxPoints: rubric.maxPoints ?? 0,
          })),
        })),
      });
    }
  }, [gradingData, isSaving]);

  const [updatedGradeEntry, setUpdatedGradeEntry] = useState<UpdateAssessmentSubmissionUpdatedGrade>({
    updatedEntityType: UpdatedEntityType.Submission,
    updatedEntityId: "",
    updatedGrade: "0",
  });

  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;

  const handleTabPress = (tab: string) => {
    setActiveTab(tab);
  };

  const handleGradeChange = async ({
    updatedEntityType,
    updatedEntityId,
    updatedGrade,
    maxPointsForEntity,
  }: {
    updatedEntityType: UpdatedEntityType;
    updatedEntityId: string;
    updatedGrade: string;
    maxPointsForEntity: number;
  }) => {
    if (isSaving || !updatedGrade || updatedGrade.trim() === "") {
      return;
    }

    // trim all non-numeric characters
    let normalizedUpdatedGrade = updatedGrade.replace(",", ".");
    normalizedUpdatedGrade = normalizedUpdatedGrade.replace(/[^0-9.]/g, "");

    const numberUpdatedGrade = normalizedUpdatedGrade;

    if (Number.isNaN(Number(numberUpdatedGrade))) {
      showToast({
        message: "Invalid score. Please enter a valid number",
        type: "error",
      });
      return;
    }

    if (Number(numberUpdatedGrade) < 0 || Number(numberUpdatedGrade) > maxPointsForEntity) {
      showToast({
        message: "Invalid score. Please enter a score between 0 and the maximum points",
        type: "error",
      });
      return;
    }

    if (updatedEntityType === UpdatedEntityType.Submission) {
      setGradeTree({
        ...gradeTree,
        totalPoints: numberUpdatedGrade,
      });
    } else if (updatedEntityType === UpdatedEntityType.Question) {
      setGradeTree({
        ...gradeTree,
        questions: gradeTree.questions.map((question) =>
          question.id === updatedEntityId ? { ...question, totalPoints: numberUpdatedGrade } : question
        ),
      });
    } else if (updatedEntityType === UpdatedEntityType.Rubric) {
      const q = gradeTree.questions.find((q) => {
        return q.rubrics.some((r) => r.id === updatedEntityId);
      });

      setGradeTree({
        ...gradeTree,
        questions: gradeTree.questions.map((question) => {
          if (question.id === q.id) {
            return {
              ...question,
              rubrics: question.rubrics.map((r) =>
                r.id === updatedEntityId ? { ...r, pointsAwarded: numberUpdatedGrade } : r
              ),
            };
          }

          return question;
        }),
      });
    }

    setUpdatedGradeEntry({
      updatedEntityType,
      updatedEntityId,
      updatedGrade: numberUpdatedGrade,
    });
  };

  const handleSave = async (modalActionPressed: boolean) => {
    if (isSaving || !gradingData) {
      return;
    }

    const currentStateId = `${updatedGradeEntry.updatedEntityType}-${updatedGradeEntry.updatedEntityId}-${updatedGradeEntry.updatedGrade}`;

    if (currentStateId === lastSavedState.current) {
      if (modalActionPressed) {
        onClose();
      }
      return;
    }

    try {
      setIsSaving(true);
      lastSavedState.current = currentStateId;

      await onSave(gradingData, updatedGradeEntry, modalActionPressed);
    } catch (error) {
      console.error("Save failed:", error);
      lastSavedState.current = "";
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CustomModal
      visible={isVisible}
      onRequestClose={onSave ? () => handleSave(true) : onClose}
      onShow={() => {}}
      ariaLabel="Assessment Responses Modal">
      <View style={styles.header}>
        <CustomText style={styles.title} text="Grade Assessment" />
      </View>

      <CustomSpacing size="xl" />
      <TabNavigator
        tabData={[
          {
            key: "submissionAndGrading",
            title: "Submission & Grading",
          },
          {
            key: "attemptHistory",
            title: "Attempt History",
          },
        ]}
        activeTab={activeTab}
        onTabPress={handleTabPress}
        activeTabColor={primaryColor}
        rightButtonComponent={null}
      />
      {activeTab === "submissionAndGrading" && (
        <SubmissionAndGradingTab
          gradeTree={gradeTree}
          gradingData={gradingData}
          isLoading={isLoading}
          handleGradeChange={handleGradeChange}
          canEditScore={canEditScore}
          handleSave={handleSave}
        />
      )}
      {activeTab === "attemptHistory" && (
        <AttemptHistoryTab assessmentId={gradingData?.assessmentId} userId={gradingData?.userId} />
      )}

      {canEditScore && updateError && (
        <View style={styles.errorContainer}>
          <CustomText text={updateError} size="s" useTranslationText={false} style={{ color: "red" }} />
        </View>
      )}

      <View style={styles.modalFooter}>
        <View style={styles.footerButtons}>
          <CustomButton
            text={"Close"}
            styleType={"primaryTransparentStroke"}
            onPress={onSave ? () => handleSave(true) : onClose}
            style={{ borderWidth: 2, borderColor: primaryColor, paddingHorizontal: 16, paddingVertical: 8 }}
            textStyle={{ color: primaryColor, fontWeight: 700 }}
          />
        </View>
      </View>
    </CustomModal>
  );
};

export default AssessmentGradingModalContainer;

const styles = StyleSheet.create({
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 16,
    marginTop: 24,
    paddingTop: 16,
    paddingHorizontal: 32,
    paddingBottom: 40,
  },
  footerButtons: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 8,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 32,
    backgroundColor: "#F2F4F7",
    width: "100%",
    alignSelf: "stretch",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  title: {
    fontSize: 20,
    fontFamily: "Inter",
    fontWeight: "600",
    color: "#344054",
  },
  errorContainer: {
    paddingHorizontal: 32,
    paddingBottom: 16,
  },
});
